// The bite log's storage layer, kept out of the component so it can be tested.
//
// Until now the log wrote four fields to localStorage and nothing ever read them back:
// load() was called by the component's own useEffect and nowhere else, so you could
// record a bite and the site would never mention it again. The timeline in the bite
// flow is the first thing that reads this data, which is why the record now carries
// enough to build one — a required date, and optional species and location.
//
// PRIVACY: this never leaves the device. There is no upload path, no sync, no id, and
// no network call anywhere in this module or its callers. Photos go to IndexedDB on the
// same device (see ./photo-store) for the same reason.

export const STORAGE_KEY = "lsm_bite_log_v2";
/** The v1 key. Read once to migrate, then left alone rather than deleted. */
export const LEGACY_STORAGE_KEY = "lsm_bite_log_v1";

export const SCHEMA_VERSION = 2;

export interface BiteEntry {
  id: string;
  /** Schema version of this record. */
  v: number;
  /** YYYY-MM-DD. Required — every window in the timeline counts from this. */
  dateFound: string;
  bodyLocation: string;
  /** Free text of where they think they picked it up. */
  exposure: string;
  /** Resolved from `exposure` when it resolved to somewhere we publish. */
  exposureStateAbbr?: string;
  /** Set when the identifier narrowed it down, and used to filter the timeline. */
  speciesSlug?: string;
  /** Key into the photo store, when a photo was kept. */
  photoId?: string;
  symptoms: string;
  createdAt: string;
}

/** What a new entry needs. Everything else is optional or generated. */
export type BiteEntryInput = Omit<BiteEntry, "id" | "v" | "createdAt">;

interface LegacyBiteEntry {
  id?: string;
  dateFound?: string;
  bodyLocation?: string;
  exposure?: string;
  symptoms?: string;
  createdAt?: string;
}

function newId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `bite-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

/**
 * Bring a stored record up to the current schema. Additive only: a v1 record keeps
 * every field it had, gains the version marker, and is given an id and timestamp if it
 * somehow lacked one. Nothing is dropped, because the person who wrote it down cannot
 * write it again.
 */
export function migrateEntry(raw: unknown): BiteEntry | null {
  if (!raw || typeof raw !== "object") return null;
  const e = raw as LegacyBiteEntry & Partial<BiteEntry>;
  if (typeof e.dateFound !== "string") return null;
  return {
    id: typeof e.id === "string" && e.id ? e.id : newId(),
    v: SCHEMA_VERSION,
    dateFound: e.dateFound,
    bodyLocation: typeof e.bodyLocation === "string" ? e.bodyLocation : "",
    exposure: typeof e.exposure === "string" ? e.exposure : "",
    exposureStateAbbr: typeof e.exposureStateAbbr === "string" ? e.exposureStateAbbr : undefined,
    speciesSlug: typeof e.speciesSlug === "string" ? e.speciesSlug : undefined,
    photoId: typeof e.photoId === "string" ? e.photoId : undefined,
    symptoms: typeof e.symptoms === "string" ? e.symptoms : "",
    createdAt: typeof e.createdAt === "string" ? e.createdAt : new Date().toISOString(),
  };
}

export function parseStored(rawJson: string | null): BiteEntry[] {
  if (!rawJson) return [];
  try {
    const parsed: unknown = JSON.parse(rawJson);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(migrateEntry).filter((e): e is BiteEntry => e !== null);
  } catch {
    return [];
  }
}

/**
 * Read the log, migrating v1 data on first run. The v1 key is deliberately left in
 * place: if anything about v2 goes wrong, the original record is still on the device.
 */
export function loadEntries(): BiteEntry[] {
  if (typeof window === "undefined") return [];
  let store: Storage;
  try {
    store = window.localStorage;
  } catch {
    return [];
  }
  const current = parseStored(store.getItem(STORAGE_KEY));
  if (current.length > 0) return current;
  if (store.getItem(STORAGE_KEY) !== null) return current;

  const legacy = parseStored(store.getItem(LEGACY_STORAGE_KEY));
  if (legacy.length > 0) saveEntries(legacy);
  return legacy;
}

export function saveEntries(entries: BiteEntry[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Private mode or quota. The flow still works for this session.
  }
}

export function createEntry(input: BiteEntryInput): BiteEntry {
  return { ...input, id: newId(), v: SCHEMA_VERSION, createdAt: new Date().toISOString() };
}

/** Most recent bite first, by the date the tick was found. */
export function sortEntries(entries: readonly BiteEntry[]): BiteEntry[] {
  return [...entries].sort(
    (a, b) => b.dateFound.localeCompare(a.dateFound) || b.createdAt.localeCompare(a.createdAt),
  );
}

/** Local-midnight Date for a YYYY-MM-DD string, avoiding UTC's off-by-one. */
export function parseDateFound(dateFound: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateFound);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

export function toIsoDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** A plain-text record to hand to a clinician. Includes every field, unlike v1's. */
export function toText(
  entries: readonly BiteEntry[],
  speciesName?: (slug: string) => string,
): string {
  const lines = [
    "LoneStarMap: tick bite log",
    "Kept on your device only. Educational record, not a medical document.",
    "",
  ];
  sortEntries(entries).forEach((e, i) => {
    lines.push(`Bite ${i + 1}`);
    lines.push(`  Date found:        ${e.dateFound || "—"}`);
    lines.push(`  Where on the body: ${e.bodyLocation || "—"}`);
    lines.push(`  Likely exposure:   ${e.exposure || "—"}`);
    if (e.speciesSlug) {
      lines.push(
        `  Possible species:  ${speciesName ? speciesName(e.speciesSlug) : e.speciesSlug}`,
      );
    }
    lines.push(`  Symptoms noted:    ${e.symptoms || "—"}`);
    if (e.photoId) lines.push("  Photo:             kept on the device");
    lines.push(`  Logged:            ${e.createdAt}`);
    lines.push("");
  });
  return lines.join("\n");
}
